import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1641199788912-9a7385a35c82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxkZWxpdmVyeSUyMHZhbiUyMGNvdXJpZXIlMjBkcml2ZXIlMjBkZWxpdmVyeSUyMHRydWNrJTIwbG9naXN0aWNzfGVufDB8fHx8MTc5MDUzODEwN3ww&ixlib=rb-4.1.0&q=85";

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-background">
    <div className="hidden lg:block relative">
      <img src={HERO} alt="Delivery fleet" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/70 to-[#0B0F17]/30" />
      <div className="absolute bottom-0 p-12 text-white">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center">
            <Icons.Truck className="h-6 w-6 text-primary-foreground" />
          </div>
          <span className="font-display text-2xl font-extrabold">Near Way</span>
        </div>
        <h2 className="font-display text-3xl font-bold max-w-md leading-tight">
          Run your parcel fleet with total financial clarity.
        </h2>
        <p className="text-white/70 mt-3 max-w-md text-sm">
          Drivers, vans, stops, fuel, tickets and profit — one clean control room built for UK courier operations.
        </p>
      </div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-10">
      <div className="w-full max-w-sm fp-fade-in">
        <div className="lg:hidden flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center">
            <Icons.Truck className="h-6 w-6 text-primary-foreground" />
          </div>
          <span className="font-display text-xl font-extrabold">Near Way</span>
        </div>
        <h1 className="font-display text-2xl font-bold text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      const msg = err.response
        ? formatApiErrorDetail(err.response?.data?.detail)
        : "Can't reach the server right now. Please wait a moment and try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your fleet control room">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="mt-1.5" data-testid="login-email-input" placeholder="you@company.co.uk" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="mt-1.5" data-testid="login-password-input" placeholder="••••••••" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
          {busy ? <Icons.LoaderCircle className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
      <p className="text-sm text-muted-foreground text-center mt-6">
        New driver?{" "}
        <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">Create account</Link>
      </p>
    </AuthShell>
  );
}
